/** Imports */

/** Green Sock - Import */
import { gsap } from 'gsap'
import { ScrollSmoother } from 'gsap/ScrollSmoother'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { SplitText } from "gsap/SplitText"

/** Splide Sliders - Import */
import Splide from '@splidejs/splide'

/** VCards (If Needed) - Import */
import vCardsJS from 'vcards-js'

/** XState Machine - Import */
import { createMachine, interpret } from '@xstate/fsm'

/** Quicklinks - Import + Listen Function */
import { listen } from 'quicklink'
listen()

/** Base Functions */

/* Green Sock - Base Functions */
gsap.registerPlugin(ScrollTrigger)
gsap.registerPlugin(SplitText)
gsap.registerPlugin(ScrollSmoother)

/* If Touch Screen */
function is_touch_enabled() {
  return ( 'ontouchstart' in window ) || 
         ( navigator.maxTouchPoints > 0 ) || 
         ( navigator.msMaxTouchPoints > 0 )
}

/* Cursor Global Functions */
function animateCursorLinks (wrapper) {
  const cursor = document.querySelector('.cross-cursor')
  const cursorDefault = cursor.querySelector('.cross-cursor__default')
  const cursorLink = cursor.querySelector('.cross-cursor__link')
  const links = wrapper.querySelectorAll('a, button, .content-image-slider__thumbnail-image, .button')

  if (links) {
    for (let index = 0; index < links.length; index++) {
      const animItem = links[index]
      
      animItem.addEventListener("mouseenter", e => {
        gsap.timeline().to(cursorDefault, {duration: 0, opacity: 0}).to(cursorLink, {duration: 0, opacity: 1}, "<")
      })
      
      animItem.addEventListener("mouseleave", e => {
        gsap.timeline().to(cursorLink, {duration: 0, opacity: 0}).to(cursorDefault, {duration: 0, opacity: 1}, "<")
      })
    }
  }
}

/* Splide - Custom Scroll Bar Init */
function sliderProgressBarInIt (sliderElement, slider, barWrapper) {
  const progressBar = barWrapper.querySelector('.splide__progress-bar')
  const lastSlide = sliderElement.querySelector('.splide__slide:last-of-type')
  const slideTrack = sliderElement.querySelector('.splide__track')
  const splideList = sliderElement.querySelector('.splide__list')
  let sliderPlacement
  let slideEnd
  let trackEnd
  let percentPerPixel
  let progressSize
  let delayFix

  // Function to set the width of the Progress Bar
  slider.on('mounted', function (data) {
    const slides = slider.Components.Slides.get(false)
    const slidesLength = slides.length
    const wrapperComputedStyle = window.getComputedStyle(slideTrack, null)
    const slidePaddingRight = parseFloat(wrapperComputedStyle.paddingRight)
    slideEnd = lastSlide.getBoundingClientRect().right
    trackEnd = slideTrack.getBoundingClientRect().right - (slidePaddingRight)
    if (slideEnd < trackEnd) {
      progressSize = 100
    } else {
      progressSize = 100 / slidesLength
    }

    progressBar.style.width = progressSize + '%'
  })

  // Setting the Slide End and Track End variable to get the percentage per pixel
  slider.on('ready', function () {
    const wrapperComputedStyle = window.getComputedStyle(slideTrack, null)
    const slidePaddingRight = parseFloat(wrapperComputedStyle.paddingRight)

    slideEnd = lastSlide.getBoundingClientRect().right
    trackEnd = slideTrack.getBoundingClientRect().right - (slidePaddingRight)
    const diff = slideEnd - trackEnd
    percentPerPixel = diff / 100
  })

  function calculateProgressBarPosition () {
    const lastSliderCardPosition = lastSlide.getBoundingClientRect().right
    let percentage

    if (lastSliderCardPosition >= slideEnd) {
      percentage = 0
    } else if (lastSliderCardPosition <= trackEnd) {
      percentage = 100
    } else {
      const pixelDiff = slideEnd - lastSliderCardPosition
      percentage = pixelDiff / percentPerPixel
    }
    const rightEdge = barWrapper.offsetWidth - progressBar.offsetWidth
    progressBar.style.left = ((rightEdge / 100) * percentage) + 'px'
  }

  // Doing some math on move / drag to move the progress bar along as the splider moves
  slider.on('move dragging', calculateProgressBarPosition)

  // Ensuring the same is done above on arrow click
  slider.on('arrows:mounted', function (prev, next) {
    prev.addEventListener('click', function () {
      delayFix = setInterval(calculateProgressBarPosition, 25)
    })

    next.addEventListener('click', function () {
      delayFix = setInterval(calculateProgressBarPosition, 25)
    })
  })

  // delay fix
  slider.on('moved dragged', function () {
    delayFix = setInterval(calculateProgressBarPosition, 25)

    setTimeout(() => {
      clearTimeout(delayFix)
    }, 500)
  })

  // Functionality to move the Progress Bar and calculate where the splider should be
  progressBar.onmousedown = function (event) {
    event.preventDefault()

    const shiftX = event.clientX - progressBar.getBoundingClientRect().left

    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)

    function onMouseMove (event) {
      let newLeft = event.clientX - shiftX - barWrapper.getBoundingClientRect().left

      // the pointer is out of progressBarWrapper => lock the progressBar within the bounaries
      if (newLeft < 0) {
        newLeft = 0
      }
      const rightEdge = barWrapper.offsetWidth - progressBar.offsetWidth
      if (newLeft > rightEdge) {
        newLeft = rightEdge
      }

      const slideScrollPercentage = (newLeft / rightEdge) * 100
      sliderPlacement = (percentPerPixel * slideScrollPercentage) * -1

      splideList.style.transform = 'translateX(' + sliderPlacement + 'px)'
      progressBar.style.left = newLeft + 'px'
    }

    function onMouseUp () {
      document.removeEventListener('mouseup', onMouseUp)
      document.removeEventListener('mousemove', onMouseMove)

      slider.Components.Controller.scroll(sliderPlacement, 0)
    }
  }

  progressBar.ondragstart = function () {
    return false
  }
}

/* Remove preload class to allow transitions */
document.body.classList.remove('preload')

/**
 * Get the value of a cookie
 * Source: https://gist.github.com/wpsmith/6cf23551dd140fb72ae7
 * @param  {String} name  The name of the cookie
 * @return {String}       The cookie value
 */
function getCookie (name) {
  const value = `; ${document.cookie}`
  const parts = value.split(`; ${name}=`)
  if (parts.length === 2) return parts.pop().split(';').shift()
}

/* Trap Focus */
function trapFocus (element, excludingIn, menuToggle) {
  const KEYCODE_TAB = 9
  element.addEventListener('keydown', function (e) {
    const isTabPressed = (e.key === 'Tab' || e.keyCode === KEYCODE_TAB)

    if (!isTabPressed) {
      return
    }

    const focusableEls = Array.from(element.querySelectorAll('a[href]:not(' + excludingIn + ' a), button:not(' + excludingIn + ' textarea):not([disabled]), .tabs a:not(' + excludingIn + ' a):not(.tabs a[aria-selected="false"]):not([disabled]), textarea:not(' + excludingIn + ' textarea):not([disabled]), input[type="text"]:not(' + excludingIn + ' input):not([disabled]), input[type="radio"]:not(' + excludingIn + ' input):not([disabled]), input[type="checkbox"]:not(' + excludingIn + ' input):not([disabled]), input[type="search"]:not(' + excludingIn + ' input):not([disabled]), input[type="submit"]:not(' + excludingIn + ' input):not([disabled]), select:not(' + excludingIn + ' select):not([disabled])'))
    
    if (focusableEls[0].classList.contains('back-button') && !focusableEls[0].classList.contains('active')) {
      focusableEls.shift()
    }

    const firstFocusableEl = focusableEls[0]
    const lastFocusableIndex = 1
    let lastFocusableEl = focusableEls[focusableEls.length - lastFocusableIndex]
    if(getComputedStyle(lastFocusableEl).display == 'none') {
      focusableEls.splice(focusableEls.length - lastFocusableIndex, 1)
      for (let index = focusableEls.length - 1; index >= 0; index--) {
        lastFocusableEl = focusableEls[index]
        if(getComputedStyle(lastFocusableEl).display != 'none') {
          break
        }
        focusableEls.splice(index, 1)
      }
    }
  })

  if (menuToggle) {
    menuToggle.addEventListener('keydown', function (e) {
      const isTabPressed = (e.key === 'Tab' || e.keyCode === KEYCODE_TAB)

      if (!isTabPressed) {
        return
      }

      const focusableEls = Array.from(element.querySelectorAll('a[href]:not(' + excludingIn + ' a), button:not(' + excludingIn + ' textarea):not([disabled]), .tabs a:not(' + excludingIn + ' a):not(.tabs a[aria-selected="false"]):not([disabled]), textarea:not(' + excludingIn + ' textarea):not([disabled]), input[type="text"]:not(' + excludingIn + ' input):not([disabled]), input[type="radio"]:not(' + excludingIn + ' input):not([disabled]), input[type="checkbox"]:not(' + excludingIn + ' input):not([disabled]), input[type="search"]:not(' + excludingIn + ' input):not([disabled]), input[type="submit"]:not(' + excludingIn + ' input):not([disabled]), select:not(' + excludingIn + ' select):not([disabled])'))
      if (focusableEls[0].classList.contains('back-button') && !focusableEls[0].classList.contains('active')) {
        focusableEls.shift()
      }

      const lastFocusableEl = focusableEls[focusableEls.length - 1]

      if (e.shiftKey) /* shift + tab */ {
        if (element.style.display == 'block') {
          e.preventDefault()
          lastFocusableEl.focus()
        }
      }
    })
  }
}

/* Custom Fade In Functions */
function fadeIns () {
  const fadeIns = gsap.utils.toArray('.animate--fadeIn')
  const fadeInUps = gsap.utils.toArray('.animate--fadeInUp')
  const fadeInLefts = gsap.utils.toArray('.animate--fadeInLeft')
  const fadeInRights = gsap.utils.toArray('.animate--fadeInRight')
  let animateStart = 'top 90%'

  // function fadeInBuilder
  function buildFadeIn (element) {
    let delayTiming = 0
    if (element.dataset.delay != null) {
      delayTiming = element.dataset.delay
    }
    const anim = gsap.fromTo(element, { opacity: 0 }, { duration: 1, opacity: 1, delay: delayTiming })
    ScrollTrigger.create({
      trigger: element,
      start: animateStart,
      animation: anim,
      toggleActions: 'play none none none',
      once: true
    })
  }

  // fade ins
  fadeIns.forEach((fadeIn) => {
    buildFadeIn(fadeIn)
  })

  // fade in ups
  fadeInUps.forEach((fadeInUp) => {
    let delayTiming = 0
    if (fadeInUp.dataset.delay != null) {
      delayTiming = fadeInUp.dataset.delay
    }

    const anim = gsap.fromTo(fadeInUp, { opacity: 0, y: 50 }, { duration: 1, opacity: 1, y: 0, delay: delayTiming })
    ScrollTrigger.create({
      trigger: fadeInUp,
      start: animateStart,
      animation: anim,
      toggleActions: 'play none none none',
      once: true
    })
  })

  // fade in lefts
  fadeInLefts.forEach((fadeInLeft) => {
    let delayTiming = 0
    if (fadeInLeft.dataset.delay != null) {
      delayTiming = fadeInLeft.dataset.delay
    }

    const anim = gsap.fromTo(fadeInLeft, { opacity: 0, x: -50 }, { duration: 1, opacity: 1, x: 0, delay: delayTiming })
    ScrollTrigger.create({
      trigger: fadeInLeft,
      start: animateStart,
      animation: anim,
      toggleActions: 'play none none none',
      once: true
    })
  })

  // fade in rights
  fadeInRights.forEach((fadeInRight) => {
    let delayTiming = 0
    if (fadeInRight.dataset.delay != null) {
      delayTiming = fadeInRight.dataset.delay
    }

    const anim = gsap.fromTo(fadeInRight, { opacity: 0, x: 50 }, { duration: 1, opacity: 1, x: 0, delay: delayTiming })
    ScrollTrigger.create({
      trigger: fadeInRight,
      start: animateStart,
      animation: anim,
      toggleActions: 'play none none none',
      once: true
    })
  })
}

/** Block / Layout Functions */

/* Header Functions */
function headerInIt () {
  // Variables
  const header = document.getElementById('header')
  const headerToggle = document.getElementById('navToggle')
  const nav = document.getElementById('headerNavMobile')
  const mainSubNavButtons = nav.querySelectorAll('.subnav-link')
  const topNav = document.getElementById('headerTop')
  const subNavCloseButtons = nav.querySelectorAll('.header__close-sub')
  let closing
  const topSubNavTimeouts = []
  const body = document.querySelector('body')
  const hamburgerTop = document.getElementById('hamburgerTop')
  const hamburgerMiddle = document.getElementById('hamburgerMiddle')
  const hamburgerBottom = document.getElementById('hamburgerBottom')
  const hamburgerTl = gsap.timeline({ paused: true })

  // Close Other SubNav Function
  function closeOtherSubNav () {
    const activeSubNavs = document.querySelectorAll('.header__main-sub.active')

    activeSubNavs.forEach(element => {
      const buttonId = element.getAttribute('aria-controlledby')
      const button = document.getElementById(buttonId)

      button.setAttribute('aria-expanded', 'false')
      button.setAttribute('aria-hidden', 'true')
      button.classList.remove('subnav-link--sub-nav-open')
      element.classList.remove('active')
      element.style.display = 'none'
    })
  }

  /* Hamburger Animation */
  
  hamburgerTl.add('start')
    .to(hamburgerTop, { rotation: 45, x: '5px', y: '-2px', transformOrigin: 'left 50%', duration: '0', ease: 'power2.out' }, 'start')
    .to(hamburgerBottom, { rotation: '-45', x: '5px', y: '2px', transformOrigin: 'left 50%', duration: '0', ease: 'power2.out' }, 'start')
    .to(hamburgerMiddle, { opacity: '0', duration: '0' }, 'start')

  /* Main Nav Header Machine */
  const headerMachine = createMachine({
    id: 'header',
    initial: 'inactive',
    states: {
      inactive: {
        on: {
          TOGGLE: {
            target: 'active',
            actions: () => {
              clearTimeout(closing)

              if (header.classList.contains('closing')) {
                header.classList.remove('closing')
              }

              nav.style.display = 'block'
              header.classList.add('active-nav')
              nav.setAttribute('aria-hidden', 'false')
              headerToggle.setAttribute('aria-expanded', 'true')
              body.classList.add('nav-active')
              hamburgerTl.play()

              headerToggle.focus()
              trapFocus(header)
            }
          }
        }
      },
      active: {
        on: {
          TOGGLE: {
            target: 'inactive',
            actions: () => {
              header.classList.remove('active-nav')
              header.classList.add('closing')
              header.classList.remove('active-subnav')
              nav.setAttribute('aria-hidden', 'true')
              headerToggle.setAttribute('aria-expanded', 'false')
              body.classList.remove('nav-active')
              const activeSubNavs = document.querySelectorAll('.header__main-sub.active')
              hamburgerTl.reverse()

              activeSubNavs.forEach(element => {
                const subNavButtonId = element.getAttribute('aria-controlledby')
                const subNavButton = document.getElementById(subNavButtonId)
                const subNavClose = element.querySelector('.header__close-sub')
                subNavButton.setAttribute('aria-expanded', 'false')
                subNavClose.setAttribute('aria-expanded', 'false')
                element.setAttribute('aria-hidden', 'true')
                element.classList.remove('active')
                nav.classList.remove('header__nav--sub-nav-open')
                setTimeout(() => {
                  element.style.display = 'none'
                  element.classList.remove('active')
                }, 300)
              })

              closing = setTimeout(() => {
                nav.style.display = 'none'
                header.classList.remove('closing')
              }, 300)

              headerToggle.focus()
            }
          }
        }
      }
    }
  })

  const headerService = interpret(headerMachine).start()

  // Main Nav Functionality
  headerToggle.addEventListener('click', () => {
    if (window.innerWidth < 768) {
      headerService.send('TOGGLE')
    }
  })

  // Main Sub Nav Functionality
  mainSubNavButtons.forEach(element => {
    element.addEventListener('click', function () {
      const subNavId = element.getAttribute('aria-controls')
      const subNav = document.getElementById(subNavId)
      const subNavClose = subNav.querySelector('.header__close-sub')
      const activeSubNavs = document.querySelectorAll('.header__main-sub.active')
      if (!subNav.classList.contains('active')) {
        if (activeSubNavs.length > 0) {
          closeOtherSubNav()
          subNav.classList.add('active')
          subNav.style.display = 'flex'
          header.classList.add('active-subnav')
          element.classList.add('subnav-link--sub-nav-open')
          element.setAttribute('aria-expanded', 'true')
          subNavClose.setAttribute('aria-expanded', 'true')
          subNav.setAttribute('aria-hidden', 'false')
          subNavClose.focus()
          trapFocus(subNav)
        } else {
          subNav.style.display = 'flex'
          header.classList.add('active-subnav')
          element.classList.add('subnav-link--sub-nav-open')
          element.setAttribute('aria-expanded', 'true')
          subNavClose.setAttribute('aria-expanded', 'true')
          subNav.setAttribute('aria-hidden', 'false')
          subNavClose.focus()
          trapFocus(subNav)
          setTimeout(() => {
            subNav.classList.add('active')
          }, 10)
        }
      }
    })
  })

  subNavCloseButtons.forEach(element => {
    element.addEventListener('click', function () {
      const subNavId = element.getAttribute('aria-controls')
      const subNav = document.getElementById(subNavId)
      const subNavButton = document.getElementById(subNavId + 'Button')
      const subNavClose = subNav.querySelector('.header__close-sub')
      if (subNav.classList.contains('active')) {
        element.setAttribute('aria-expanded', 'false')
        header.classList.remove('active-subnav')
        subNavClose.setAttribute('aria-expanded', 'false')
        subNav.setAttribute('aria-hidden', 'true')
        subNav.classList.remove('active')
        subNavButton.classList.remove('subnav-link--sub-nav-open')
        subNavButton.focus()
        setTimeout(() => {
          subNav.style.display = 'none'
        }, 300)
      }
    })
  })

  // Top Nav Functionality
  if (topNav) {
    const topSubNavButtons = topNav.querySelectorAll('.subnav-link')

    topSubNavButtons.forEach((element, i) => {
      element.addEventListener('click', function () {
        const subNavId = element.getAttribute('aria-controls')
        const subNav = document.getElementById(subNavId)

        if (topSubNavTimeouts[i] != null) {
          clearTimeout(topSubNavTimeouts[i])
          topSubNavTimeouts[i] = null
        }

        if (!subNav.classList.contains('active')) {
          subNav.style.display = 'block'
          element.setAttribute('aria-expanded', 'true')
          subNav.setAttribute('aria-hidden', 'false')
          setTimeout(() => {
            subNav.classList.add('active')
          }, 10)
        } else {
          element.setAttribute('aria-expanded', 'false')
          subNav.setAttribute('aria-hidden', 'true')
          subNav.classList.remove('active')
          subNav.style.display = 'none'
        }
      })

      element.parentElement.addEventListener('mouseover', function () {
        const subNavId = element.getAttribute('aria-controls')
        const subNav = document.getElementById(subNavId)

        if (topSubNavTimeouts[i] != null) {
          clearTimeout(topSubNavTimeouts[i])
          topSubNavTimeouts[i] = null
        }

        if (!subNav.classList.contains('active')) {
          subNav.style.display = 'block'
          element.setAttribute('aria-expanded', 'true')
          subNav.setAttribute('aria-hidden', 'false')
          setTimeout(() => {
            subNav.classList.add('active')
          }, 10)
        }
      })

      element.parentElement.addEventListener('mouseout', function () {
        const subNavId = element.getAttribute('aria-controls')
        const subNav = document.getElementById(subNavId)

        if (topSubNavTimeouts[i] != null) {
          clearTimeout(topSubNavTimeouts[i])
          topSubNavTimeouts[i] = null
        }

        if (subNav.classList.contains('active')) {
          topSubNavTimeouts[i] = setTimeout(() => {
            element.setAttribute('aria-expanded', 'false')
            subNav.setAttribute('aria-hidden', 'true')
            subNav.classList.remove('active')
            subNav.style.display = 'none'
          }, 300)
        }
      })
    })
  }

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') {

      if (header.classList.contains('active-nav')) {
        headerService.send('TOGGLE')
      }
    }
  })

  window.addEventListener('resize', function (event) {
    if (window.innerWidth > 768 && header.classList.contains('active-nav')) {
      headerService.send('TOGGLE')
    }
  })

  // Header Scrolling
  const initScrollPos = window.scrollY || window.scrollTop || document.getElementsByTagName('html')[0].scrollTop
  let lastScrollPos = initScrollPos;

  if (initScrollPos > header.offsetHeight) {
    body.classList.add('header--scrolling')
  }

  window.addEventListener('scroll', function () {
    const scrollPos = window.scrollY || window.scrollTop || document.getElementsByTagName('html')[0].scrollTop

    if (scrollPos > header.offsetHeight) {
      if (!body.classList.contains('header--scrolling')) {
        body.classList.add('header--scrolling')
      }

      if ((scrollPos < lastScrollPos) && (!header.classList.contains('header--visible'))) {
        header.classList.add('header--visible')
      } else if ((scrollPos > lastScrollPos) && (header.classList.contains('header--visible'))) {
        header.classList.remove('header--visible')
      }
    } else if (scrollPos == 0) {
      if (body.classList.contains('header--scrolling')) {
        body.classList.remove('header--scrolling')
      }
    }

    lastScrollPos = scrollPos <= 0 ? 0 : scrollPos
  })
}

/* Content Card Slider Functions */
function contentCardSliderInIt () {
  // Variables
  const wrappers = document.querySelectorAll('.content-card-slider')

  wrappers.forEach(element => {
    const sliderWrapper = element.querySelector('.content-card-slider__wrapper.splide')

    // Initializing Spliders
    const progressBarWrapper = sliderWrapper.querySelector('.splide__progress-bar-wrapper')
    const slider = new Splide(sliderWrapper, {
      perPage: 1,
      arrows: true,
      gap: '1rem',
      padding: '1.5rem',
      drag: 'free',
      pagination: false,
      mediaQuery: 'min',
      breakpoints: {
        992: {
          arrows: true,
          padding: '6rem',
          perPage: 3
        },
        767: {
          arrows: true,
          perPage: 2
        },
        481: {
          arrows: true,
          gap: '2rem',
          padding: '2.5rem',
          perPage: 1
        }
      }
    })

    if (progressBarWrapper) {
      sliderProgressBarInIt(sliderWrapper, slider, progressBarWrapper)
    }

    slider.mount()
  })
}

/* Content Half Content Media Functions */
function halfContentMediaScaleInIt () {
  let wrappers = document.querySelectorAll('.content-half-content-media, .content-half-team-content, .callout-contact, .content-approach-steps')

  for (let index = 0; index < wrappers.length; index++) {
    const wrapper = wrappers[index]
    const halfImageWrapper = wrapper.querySelector('.content-half__media--image')

    if (halfImageWrapper) {
      const image = halfImageWrapper.querySelector('img')
      
      var imageTl = gsap.timeline({ scrollTrigger: {
        trigger: wrapper,
        scrub: 1,
        start: 'top center',
        end: 'bottom center',
        // markers: true
      }}).to(image, {scale: 1.075})
    }
    
  }
}

/* Content - Image Slider Functions */
function contentImageSliderInIt () {
  // Variables
  const wrappers = document.getElementsByClassName('content-image-slider')

  for (let index = 0; index < wrappers.length; index++) {
    const wrapper = wrappers[index]
    const mainSlider = wrapper.querySelector('.content-image-slider__main-slider')
    const thumbSlider = wrapper.querySelector('.content-image-slider__thumbnail-slider.splide')
    const captions = wrapper.querySelector('.content-image-slider__captions')

    if (mainSlider || thumbSlider) {
      let mainSplide = false
      let thumbSplide = false

      if (thumbSlider) {
        thumbSplide = new Splide(thumbSlider, {
          fixedWidth  : 90,
          gap         : 15,
          rewind      : true,
          pagination  : false,
          isNavigation: true,
          arrows      : true,
        } )
      }

      if (mainSlider) {
        mainSplide = new Splide(mainSlider, {
          type      : 'fade',
          rewind    : true,
          pagination: false,
          arrows    : false,
        } )

        if (captions) {
          let captionsTimeOut = false

          mainSplide.on( 'move', function (newIndex) {
            const pastCaption = captions.querySelector('.content-image-slider__caption--active')
            const activeCaption = captions.querySelector('.content-image-slider__caption[data-slide="' + newIndex + '"]')

            if (captionsTimeOut) {
              clearTimeout(captionsTimeOut)
              captionsTimeOut = false
            }

            captions.classList.add('content-image-slider__captions--fade-out')

            captionsTimeOut = setTimeout(() => {
              if (pastCaption) {
                pastCaption.classList.remove('content-image-slider__caption--active')
              }
              if (activeCaption) {
                activeCaption.classList.add('content-image-slider__caption--active')
              }

              captions.classList.remove('content-image-slider__captions--fade-out')
              captions.classList.add('content-image-slider__captions--fade-in')

              setTimeout(() => {
                captions.classList.remove('content-image-slider__captions--fade-in')
              }, 250)
            }, 250)
          })
        }
      }

      if (thumbSplide) {
        mainSplide.sync( thumbSplide )
        mainSplide.mount()
        thumbSplide.mount()
      } else {
        mainSplide.mount()
      }
    }
    
  }
}

/* Content - Video Slider Functions */
function contentVideoSliderInIt () {
  // Variables
  const wrappers = document.getElementsByClassName('content-video-slider')

  for (let index = 0; index < wrappers.length; index++) {
    const wrapper = wrappers[index]
    const mainSlider = wrapper.querySelector('.content-video-slider__main-slider')
    const captions = wrapper.querySelector('.content-video-slider__captions')

    let mainSplide = false

    if (mainSlider) {
      mainSplide = new Splide(mainSlider, {
        type      : 'fade',
        rewind    : true,
        pagination: true,
        arrows    : true,
        perPage : 1,
      } ).mount()

      if (captions) {
        let captionsTimeOut = false

        mainSplide.on( 'move', function (newIndex) {
          const pastCaption = captions.querySelector('.content-video-slider__caption--active')
          const activeCaption = captions.querySelector('.content-video-slider__caption[data-slide="' + newIndex + '"]')

          if (captionsTimeOut) {
            clearTimeout(captionsTimeOut)
            captionsTimeOut = false
          }

          captions.classList.add('content-video-slider__captions--fade-out')

          captionsTimeOut = setTimeout(() => {
            if (pastCaption) {
              pastCaption.classList.remove('content-video-slider__caption--active')
            }
            if (activeCaption) {
              activeCaption.classList.add('content-video-slider__caption--active')
            }

            captions.classList.remove('content-video-slider__captions--fade-out')
            captions.classList.add('content-video-slider__captions--fade-in')

            setTimeout(() => {
              captions.classList.remove('content-video-slider__captions--fade-in')
            }, 250)
          }, 250)
        })
      }
    }
  }
}

/* Logo Grid Functions */
function logoGridInIt () {
  // Variables
  const sliders = document.getElementsByClassName('content-logo-grid__logos-grid splide')

  // Initializing Splider
  for (let i = 0; i < sliders.length; i++) {
    new Splide(sliders[i], {
      type: 'loop',
      perPage: 4,
      perMove: 1,
      rewind: true,
      arrows: false,
      gap: '3rem',
      breakpoints: {
        992: {
          gap: '2rem'
        },
        768: {
          perPage: 2,
          gap: '1rem'
        }
      }
    }).mount()
  }
}

/* Clients Grid Functions */
function clientsGridInIt () {
  // Variables
  const sliders = document.getElementsByClassName('content-clients-grid__clients-grid splide')

  // Initializing Splider
  for (let i = 0; i < sliders.length; i++) {
    new Splide(sliders[i], {
      type: 'loop',
      perPage: 5,
      perMove: 1,
      rewind: true,
      arrows: false,
      gap: '2rem',
      breakpoints: {
        1200: {
          perPage: 3,
          gap: '2rem'
        },
        768: {
          perPage: 2,
          gap: '1rem'
        }
      }
    }).mount()
  }
}

/* Content Quote Functions */
function contentQuoteInIt () {
  // Variables
  const sliders = document.getElementsByClassName('content-quote__slider')

  // Initializing Splider
  for (let i = 0; i < sliders.length; i++) {
    new Splide(sliders[i], {
      type: 'loop',
      perPage: 1,
      perMove: 1,
      gap: '1rem',
      rewind: true,
      arrows: true
    }).mount()
  }
}

/* Hero - Home Functions */
function heroHomeInIt() {
  // Variables
  const body = document.querySelector('body')
  const hero = document.querySelector('.hero-home')
  const content = hero.querySelector('.hero-home__content-text')
  const logo = hero.querySelector('.hero-home__logo')
  const lineWrapper = hero.querySelector('.hero-home__content-line')
  const line = hero.querySelector('.hero-home__content-line-revealer')
  const nav = document.getElementById('headerHome')
  const preloader = document.querySelector('.preload-wrapper')
  const arrow = hero.querySelector('.hero-home__scroll-down')
  const tl = gsap.timeline()

  function arrowLoopTl() {
    const arrowLoopTl = gsap.timeline({repeat: -1})
    arrowLoopTl.to(arrow, {duration: 1.5, opacity: 1, y: 0})
      .to(arrow, {duration: .5, opacity: 0, delay: 2})
    return arrowLoopTl
  }

  if(preloader) {
    const preloaderLogo = preloader.querySelector('.preload-wrapper__logo')
    const logoPreloadEst = preloaderLogo.querySelector('.logo-est__est')

    function preloaderCleanup() {
      preloader.remove()
      body.classList.remove('modal-active')
    }

    function scrollToTop() {
      window.scrollTo(0,0)
    }

    body.classList.add('modal-active')
  
    tl.to(preloaderLogo, {duration: 1, opacity: 1, scale: 1, ease:"power1.inOut", delay: 1})
      .from(logoPreloadEst, {duration: .5, opacity: 0, delay: .25}, "<")
      .call(scrollToTop)
      .to(preloader, {duration: .5, opacity: 0})
    
  }

  if(content) {
    const split = new SplitText(content, {
      type: "lines",
      linesClass: "hodges-content-split"
    })

    tl.from(split.words, {
      duration: .75,
      opacity: 0,
      ease:"power4.inOut",
      stagger: 0.1,
      delay: .05
    }, "<").to(line, {duration: 1.25, width: 0, ease:"power4.inOut", delay: .25}, "<")
  } else if(logo) {
    const logoTitle = logo.querySelector('.logo-est__title')
    const logoEst = logo.querySelector('.logo-est__est')

    tl.to(logoTitle, {duration: .75, y: '-11px', ease:"power1.inOut", delay: .15}, "<")
      .to(logoEst, {duration: .75, y: '10px', ease:"power1.inOut", delay: .05}, "<")
      .to(lineWrapper, {duration: 1, opacity: .7, delay: .25}, "<")
      .to(line, {duration: 1, width: 0, ease:"power4.inOut"}, "<")
  }

  tl.fromTo(nav, {opacity: 0}, {duration: .5, opacity: 1, ease: "power1.inOut", delay: 1}, "<")

  tl.call(preloaderCleanup)

  if(arrow) {
    tl.add(arrowLoopTl)
  }
}

/* Team Card Functions */
function teamCardInit () {
  // variables
  const teamCards = document.querySelectorAll('.card-team')

  teamCards.forEach((team, i) => {
    window['vCard' + i] = vCardsJS()
    const vCardButton = team.querySelector('.vcard-button')
    const phoneNumberButton = team.querySelector('.card-team__phone-number-button')

    // set up the V Card
    if (vCardButton.dataset.name !== undefined && vCardButton.dataset.name !== '') {
      const name = vCardButton.dataset.name.split(' ')
      const nameTotal = name.length

      if (nameTotal > 1) {
        window['vCard' + i].firstName = name[0]
        window['vCard' + i].lastName = name[nameTotal - 1]
        if (nameTotal >= 3) {
          let middleNameStr = ''
          for (let index = 1; index < nameTotal; index++) {
            middleNameStr += name[index]

            if (index !== (nameTotal - 1)) {
              middleNameStr += ' '
            }
          }
          window['vCard' + i].middleName = middleNameStr
        }
      } else {
        window['vCard' + i].firstName = name[0]
      }
    }

    window['vCard' + i].organization = 'COMPANY NAME'
    if (vCardButton.dataset.img !== undefined && vCardButton.dataset.img !== '') {
      window['vCard' + i].photo.attachFromUrl(vCardButton.dataset.img, 'JPEG')
    }
    if (vCardButton.dataset.phone !== undefined && vCardButton.dataset.phone !== '') {
      window['vCard' + i].workPhone = vCardButton.dataset.phone
    }
    if (vCardButton.dataset.email !== undefined && vCardButton.dataset.email !== '') {
      window['vCard' + i].workEmail = vCardButton.dataset.email
    }
    if (vCardButton.dataset.title !== undefined && vCardButton.dataset.title !== '') {
      window['vCard' + i].title = vCardButton.dataset.title
    }

    function saveAs (uri, filename) {
      const link = document.createElement('a')
      if (typeof link.download === 'string') {
        link.href = uri
        link.download = filename

        // Firefox requires the link to be in the body
        document.body.appendChild(link)

        // simulate click
        link.click()

        // remove the link when done
        document.body.removeChild(link)
      } else {
        window.open(uri)
      }
    }

    vCardButton.addEventListener('click', function (e) {
      e.preventDefault()
      saveAs('data:text/x-vcard;urlencoded,' + encodeURIComponent(window['vCard' + i].getFormattedString()), `${String(window['vCard' + i].firstName).toLowerCase()}-${String(window['vCard' + i].lastName).toLowerCase()}.vcf`)
    })

    if (phoneNumberButton) {
      let closingPhone = false

      phoneNumberButton.addEventListener('click', function () {
        const phoneNumberWrapperId = phoneNumberButton.getAttribute('aria-controls')
        const phoneNumberWrapper = document.getElementById(phoneNumberWrapperId)

        if (!phoneNumberWrapper.classList.contains('card-team__phone-number--active')) {
          if (closingPhone !== false) {
            clearTimeout(closingPhone)
          }

          // close active number wrapper
          if (document.querySelector('.card-team__phone-number--active')) {
            const activeNumber = document.querySelector('.card-team__phone-number--active')
            const activeNumberButtonId = activeNumber.getAttribute('aria-controlledBy')
            const activeNumberButton = document.getElementById(activeNumberButtonId)

            activeNumber.classList.remove('card-team__phone-number--active')
            activeNumber.setAttribute('aria-hidden', 'true')
            activeNumberButton.setAttribute('aria-expanded', 'false')

            closingPhone = setTimeout(() => {
              activeNumber.style.display = 'none'
            }, 100)
          }

          // open this wrapper
          phoneNumberWrapper.style.display = 'block'
          phoneNumberWrapper.setAttribute('aria-hidden', 'false')
          phoneNumberButton.setAttribute('aria-expanded', 'true')

          setTimeout(() => {
            phoneNumberWrapper.classList.add('card-team__phone-number--active')
          }, 100)
        } else {
          phoneNumberWrapper.classList.remove('card-team__phone-number--active')
          phoneNumberWrapper.setAttribute('aria-hidden', 'true')
          phoneNumberButton.setAttribute('aria-expanded', 'false')

          closingPhone = setTimeout(() => {
            phoneNumberWrapper.style.display = 'none'
          }, 100)
        }
      })
    }
  })
}

/* Related - Projects Functions */
function relatedProjectsInIt () {
  // Variables
  const wrappers = document.getElementsByClassName('related-projects')

  for (let index = 0; index < wrappers.length; index++) {
    const wrapper = wrappers[index]
    const projectLinks = wrapper.querySelectorAll('.related-projects__list-link')
    const sliderWrapper = wrapper.querySelector('.related-projects__list-images.splide')

    if (projectLinks) {
      let imagesWrapperTimeOut = false

      projectLinks.forEach(element => {
        element.addEventListener("mouseenter", e => {
          if (window.innerWidth > 767) {
            const pastLink = wrapper.querySelector('.related-projects__list-link--active')
            const pastImage = wrapper.querySelector('.related-projects__list-image--active')
            const newImageId = element.dataset.image
            const newImage = wrapper.querySelector('.related-projects__list-image[data-link="' + newImageId + '"]')
    
            pastLink.classList.remove('related-projects__list-link--active')
            element.classList.add('related-projects__list-link--active')
    
            if (newImage && (newImage !== pastImage)) {
              if (imagesWrapperTimeOut) {
                clearTimeout(imagesWrapperTimeOut)
                imagesWrapperTimeOut = false
              }
    
              imagesWrapperTimeOut = setTimeout(() => {
                if (pastImage) {
                  pastImage.classList.remove('related-projects__list-image--active')
                  pastImage.setAttribute('aria-hidden', 'true')
                }
                newImage.classList.add('related-projects__list-image--active')
                newImage.setAttribute('aria-hidden', 'false')
              }, 150)
            }
          }
        })
      })
    }

    if (sliderWrapper) {
      const slider = new Splide(sliderWrapper, {
        perPage: 1,
        arrows: true,
        gap: '1rem',
        pagination: true,
        mediaQuery: 'min',
        type: 'loop',
        breakpoints: {
          767: {
            destroy: true
          }
        }
      }).mount()
    }
  }
}

/* Line Revealer Functions */
function lineRevealerInit () {
  // variables
  const lineRevealers = document.querySelectorAll('.content-line')

  for (let index = 0; index < lineRevealers.length; index++) {
    const wrapper = lineRevealers[index]
    const revealer = wrapper.querySelector('.content-line-revealer')
    let animateStart = 'top 90%'
  
    if (window.innerWidth > 767) {
      animateStart = 'top 70%'
    }

    if(revealer) {
      const anim = gsap.fromTo(revealer, { width: '100%' }, { width: '0%', delay: 1 })
      ScrollTrigger.create({
        trigger: wrapper,
        start: animateStart,
        animation: anim,
        toggleActions: 'play none none none',
        once: true
      })
    }    
  }
}

/* Single Team Functions */
function teamSingleInit () {
  // variables
  const teamSingle = document.querySelectorAll('.content-team-single')

  teamSingle.forEach((team, i) => {
    window['vCard' + i] = vCardsJS()
    const vCardButton = team.querySelector('.vcard-button')
    const phoneNumberButton = team.querySelector('.card-team__phone-number-button')

    // set up the V Card
    if (vCardButton.dataset.name !== undefined && vCardButton.dataset.name !== '') {
      const name = vCardButton.dataset.name.split(' ')
      const nameTotal = name.length

      if (nameTotal > 1) {
        window['vCard' + i].firstName = name[0]
        window['vCard' + i].lastName = name[nameTotal - 1]
        if (nameTotal >= 3) {
          let middleNameStr = ''
          for (let index = 1; index < nameTotal; index++) {
            middleNameStr += name[index]

            if (index !== (nameTotal - 1)) {
              middleNameStr += ' '
            }
          }
          window['vCard' + i].middleName = middleNameStr
        }
      } else {
        window['vCard' + i].firstName = name[0]
      }
    }

    window['vCard' + i].organization = 'COMPANY NAME'
    if (vCardButton.dataset.img !== undefined && vCardButton.dataset.img !== '') {
      window['vCard' + i].photo.attachFromUrl(vCardButton.dataset.img, 'JPEG')
    }
    if (vCardButton.dataset.phone !== undefined && vCardButton.dataset.phone !== '') {
      window['vCard' + i].workPhone = vCardButton.dataset.phone
    }
    if (vCardButton.dataset.email !== undefined && vCardButton.dataset.email !== '') {
      window['vCard' + i].workEmail = vCardButton.dataset.email
    }
    if (vCardButton.dataset.title !== undefined && vCardButton.dataset.title !== '') {
      window['vCard' + i].title = vCardButton.dataset.title
    }

    function saveAs (uri, filename) {
      const link = document.createElement('a')
      if (typeof link.download === 'string') {
        link.href = uri
        link.download = filename

        // Firefox requires the link to be in the body
        document.body.appendChild(link)

        // simulate click
        link.click()

        // remove the link when done
        document.body.removeChild(link)
      } else {
        window.open(uri)
      }
    }

    vCardButton.addEventListener('click', function (e) {
      e.preventDefault()
      saveAs('data:text/x-vcard;urlencoded,' + encodeURIComponent(window['vCard' + i].getFormattedString()), `${String(window['vCard' + i].firstName).toLowerCase()}-${String(window['vCard' + i].lastName).toLowerCase()}.vcf`)
    })

    if (phoneNumberButton) {
      let closingPhone = false

      phoneNumberButton.addEventListener('click', function () {
        const phoneNumberWrapperId = phoneNumberButton.getAttribute('aria-controls')
        const phoneNumberWrapper = document.getElementById(phoneNumberWrapperId)

        if (!phoneNumberWrapper.classList.contains('card-team__phone-number--active')) {
          if (closingPhone !== false) {
            clearTimeout(closingPhone)
          }

          // close active number wrapper
          if (document.querySelector('.card-team__phone-number--active')) {
            const activeNumber = document.querySelector('.card-team__phone-number--active')
            const activeNumberButtonId = activeNumber.getAttribute('aria-controlledBy')
            const activeNumberButton = document.getElementById(activeNumberButtonId)

            activeNumber.classList.remove('card-team__phone-number--active')
            activeNumber.setAttribute('aria-hidden', 'true')
            activeNumberButton.setAttribute('aria-expanded', 'false')

            closingPhone = setTimeout(() => {
              activeNumber.style.display = 'none'
            }, 100)
          }

          // open this wrapper
          phoneNumberWrapper.style.display = 'block'
          phoneNumberWrapper.setAttribute('aria-hidden', 'false')
          phoneNumberButton.setAttribute('aria-expanded', 'true')

          setTimeout(() => {
            phoneNumberWrapper.classList.add('card-team__phone-number--active')
          }, 100)
        } else {
          phoneNumberWrapper.classList.remove('card-team__phone-number--active')
          phoneNumberWrapper.setAttribute('aria-hidden', 'true')
          phoneNumberButton.setAttribute('aria-expanded', 'false')

          closingPhone = setTimeout(() => {
            phoneNumberWrapper.style.display = 'none'
          }, 100)
        }
      })
    }
  })
}

/* Single Post Related Posts Slider */
function postRelatedSlider () {
  const wrapper = document.querySelector('.single-post__related-content-wrapper.splide')

  // Initializing Spliders
  const progressBarWrapper = wrapper.querySelector('.splide__progress-bar-wrapper')
  const slider = new Splide(wrapper, {
    perPage: 1,
    arrows: true,
    gap: '1rem',
    drag: 'free',
    pagination: false,
    mediaQuery: 'min',
    padding: '1rem',
    breakpoints: {
      767: {
        destroy: true
      },
      481: {
        gap: '2rem',
        perPage: 2,
        padding: '2.5rem'
      }
    }
  })

  if (progressBarWrapper) {
    sliderProgressBarInIt(wrapper, slider, progressBarWrapper)
  }

  slider.mount()
}

/* Cross Cursor Functions */
function crossCursorInIt () {
  const cursor = document.querySelector('.cross-cursor')
  const cursorDefault = cursor.querySelector('.cross-cursor__default')
  const cursorLink = cursor.querySelector('.cross-cursor__link')
  const animHover = document.querySelectorAll('a, button, .content-image-slider__thumbnail-image, .button, video')
  const iframes = document.querySelectorAll('iframe')
  let fadeInCursor = true
  
  gsap.set(cursor, {xPercent: -50, yPercent: -50});

  let xSetter = gsap.quickTo(cursor, "x", {duration: 0.15, ease: "power2"})
  let ySetter = gsap.quickTo(cursor, "y", {duration: 0.15, ease: "power2"})

  window.addEventListener("mousemove", e => {  
    xSetter(e.x)
    ySetter(e.y)

    if(!is_touch_enabled()) {
      if (fadeInCursor) {
        gsap.to(cursor, {opacity: 1, duration: 1, delay: .15})
        fadeInCursor = false
      }
    }
  })

  if (animHover) {
    for (let index = 0; index < animHover.length; index++) {
      const animItem = animHover[index]
      
      animItem.addEventListener("mouseenter", e => {
        gsap.timeline().to(cursorDefault, {duration: 0, opacity: 0}).to(cursorLink, {duration: 0, opacity: 1}, "<")
      })
      
      animItem.addEventListener("mouseleave", e => {
        gsap.timeline().to(cursorLink, {duration: 0, opacity: 0}).to(cursorDefault, {duration: 0, opacity: 1}, "<")
      })
    }
  }

  if (iframes) {
    for (let o = 0; o < iframes.length; o++) {
      const iframe = iframes[o]
      
      iframe.addEventListener("mouseenter", e => {
        gsap.timeline().to(cursorDefault, {duration: 0, opacity: 0}).to(cursorLink, {duration: 0, opacity: 0}, "<")
      })
      
      iframe.addEventListener("mouseleave", e => {
        gsap.timeline().to(cursorLink, {duration: 0, opacity: 0}).to(cursorDefault, {duration: 0, opacity: 1}, "<")
      })
    }
  }
}

/* Initialize Everything */
document.addEventListener('DOMContentLoaded', function () {
  if (document.getElementById('header')) {
    headerInIt()
  }

  /* Fade Ins */
  fadeIns()

  /* Content - Card Slider */
  if (document.getElementsByClassName('content-card-slider').length > 0) {
    contentCardSliderInIt()
  }

  /* Content - Half Content Media || Content - Half Team Content || Callout - Contact */
  if (document.getElementsByClassName('content-half-content-media').length > 0 || document.getElementsByClassName('content-half-team-content').length > 0 || document.getElementsByClassName('callout-contact').length > 0 || document.getElementsByClassName('content-approach-steps').length > 0) {
    halfContentMediaScaleInIt()
  }

  /* Content - Image Slider */
  if (document.getElementsByClassName('content-image-slider').length > 0) {
    contentImageSliderInIt()
  }

  /* Content - Video Slider */
  if (document.getElementsByClassName('content-video-slider').length > 0) {
    contentVideoSliderInIt()
  }

  /* Content - Logo Grid */
  if (document.getElementsByClassName('content-logo-grid__logos-grid splide').length > 0) {
    logoGridInIt()
  }

  /* Content - Clients Grid */
  if (document.getElementsByClassName('content-clients-grid__clients-grid splide').length > 0) {
    clientsGridInIt()
  }

  /* Content - Quote */
  if (document.getElementsByClassName('content-quote__slider').length > 0) {
    contentQuoteInIt()
  }

  /* Hero - Home */
  if (document.getElementsByClassName('hero-home').length > 0) {
    heroHomeInIt()
  }

  /* Team Card */
  if (document.getElementsByClassName('card-team').length > 0) {
    teamCardInit()
  }

  /* Related Projects */
  if (document.getElementsByClassName('related-projects').length > 0) {
    relatedProjectsInIt()
  }

  /* Team Single */
  if (document.getElementsByClassName('content-team-single').length > 0) {
    teamSingleInit()
  }

  /* Single Post Related Slider */
  if (document.getElementsByClassName('single-post__related').length > 0) {
    postRelatedSlider()
  }

  /* Line Revealer */
  if (document.getElementsByClassName('content-line').length > 0) {
    lineRevealerInit()
  }

  /* Circle Cursor */
  if (document.getElementsByClassName('cross-cursor').length > 0) {
    crossCursorInIt()
  }

  let urlHash = window.location.href.split("#")[1]
  let scrollElem = document.querySelector("#" + urlHash)

  if (urlHash && scrollElem) {
    let currentScrollTop = document.documentElement.scrollTop || document.body.scrollTop
    let topBounding = scrollElem.getBoundingClientRect().top
    
    let smoother = ScrollSmoother.create({
      smooth: 2,
    })


    if(currentScrollTop != topBounding) {
      gsap.to(smoother, {
        scrollTop: smoother.offset(scrollElem, "top top"),
        duration: .5,
        delay: 0.5
      })
    }
  }
})
